<script>
import AppButton from '~/components/UI/app-button/AppButton.vue'

import { lockScroll, unlockScroll } from '../../../../../zendrop-vue3/src/utils/scrollLock'


export default {
  components: {
    AppButton,
  },
  emits: ['close', 'input', 'negative-click', 'positive-click'],
  props: {
    title: {
      type: String,
      required: false,
      default: '',
    },
    titlePosition: {
      type: String,
      default: 'center',
      validator: (value) => ['left', 'center'].includes(value),
    },
    message: {
      type: String,
      required: false,
      default: '',
    },
    cancelButtonText: {
      type: String,
      required: false,
      default: '',
    },
    confirmButtonText: {
      type: String,
      required: false,
      default: '',
    },
    value: {
      type: Boolean,
      required: true,
    },
    image: {
      type: String,
      default: '',
    },
    maxWidth: {
      type: String,
      default: '420px',
    },
    confirmButtonVariant: {
      type: String,
      default: 'mulberry',
      validator: (value) => ['primary', 'secondary', 'yellow', 'white', 'red', 'mulberry'].includes(value),
    },
    closable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      scrollLocked: false,
    }
  },
  computed: {
    isAnswerModal() {
      return this.confirmButtonText && this.cancelButtonText
    },
  },
  watch: {
    value(isOpen) {
      this.setScrollLock(isOpen)
    },
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeyDown)
    if (this.value) this.setScrollLock(true)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyDown)
    this.setScrollLock(false)
  },
  methods: {
    setScrollLock(shouldLock) {
      if (shouldLock === this.scrollLocked) return
      this.scrollLocked = shouldLock
      shouldLock ? lockScroll() : unlockScroll()
    },
    handleKeyDown(e) {
      if (e.key === 'Escape') this.closeModal()
    },
    closeModal(isForceClose = false) {
      if (!this.closable && !isForceClose) return
      this.$emit('close', false)
      this.$emit('input', false)
    },
    negativeClick() {
      this.$emit('negative-click')
      this.closeModal(true)
    },
    positiveClick() {
      this.$emit('positive-click')
      this.closeModal(true)
    },
  },
}
</script>

<template>
  <div
    v-if="value"
    class="dialog-overlay"
    @click.self="closable && closeModal()"
  >
    <div
      class="dialog-window"
      :style="{ maxWidth }"
    >
      <div class="dialog-head" :class="titlePosition">
        <slot name="header">
          <div
            v-if="title"
            class="dialog-title"
            :class="titlePosition"
          >
            <h3 class="font-22-600">
              {{ title }}
            </h3>
          </div>
        </slot>
        <app-button
          v-if="closable"
          class="dialog-close"
          prepend-icon="Close"
          icon
          @click="closeModal"
        />
      </div>

      <slot name="image-slot">
        <div
          v-if="image"
          class="dialog-image-wrapper"
        >
          <img
            class="dialog-image"
            :src="image"
          >
        </div>
      </slot>

      <slot name="body">
        <div v-if="message">
          <div
            v-dompurify-html="message"
            class="dialog-text"
          />
        </div>

        <div
          class="dialog-action-block"
          :class="{ answer: isAnswerModal }"
        >
          <app-button
            v-if="cancelButtonText"
            variant="secondary"
            @click="negativeClick"
          >
            {{ cancelButtonText }}
          </app-button>

          <app-button
            :variant="confirmButtonVariant || 'mulberry'"
            @click="positiveClick"
          >
            {{ confirmButtonText }}
          </app-button>
        </div>
      </slot>
    </div>
  </div>
</template>

<style scoped lang="scss">
.dialog {
  &-overlay {
    position: fixed;
    z-index: 9000;
    top: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    background-color: rgb(0 0 0 / 50%);
    font-family: 'Proxima Nova Regular', sans-serif;
  }

  &-window {
    position: relative;
    margin: 0 12px;
    width: 100%;

    // Content taller than the viewport must scroll inside the window: the page scroll is
    // locked while the modal is open, so an overflowing footer is unreachable otherwise.
    max-height: calc(100dvh - 48px);
    padding: 24px 32px 32px;
    border-radius: 16px;
    background-color: var(--neutral-8);
    box-shadow: 0 2px 8px rgb(0 0 0 / 26%);
    overflow-y: auto;
  }

  &-head {
    position: relative;
    margin-bottom: 24px;
    display: flex;
    min-height: 32px;

    &.center {
      justify-content: center;
    }
  }

  &-title {
    display: flex;
    align-items: center;
    border: none;

    &.left {
      padding: 0 42px 0 0;
    }

    &.center {
      justify-content: center;
      padding: 0 42px;
    }

    h3 {
      margin-bottom: 0;
      letter-spacing: 1px;
      text-align: center;
    }
  }

  &-close {
    position: absolute;
    top: 0;
    right: 0;
    background-color: var(--neutral-7);
  }

  &-image-wrapper {
    display: flex;
    justify-content: center;
  }

  &-image {
    margin: 0 0 24px;
    max-width: 100%;
  }

  &-text {
    margin-bottom: 32px;
    color: var(--color);
    font-size: 16px;
    font-weight: 400;
    line-height: 20px;
    text-align: center;
  }

  &-action-block {
    margin: 0;
    display: flex;
    justify-content: center;
    gap: 16px;

    &.answer > .app-button {
      flex: 1;
    }
  }
}

@media (width <= 425px) {
  .dialog {
    &-window {
      margin: 24px;
      padding: 24px 16px;
    }
  }
}
</style>
